import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Button,
  Checkbox,
  DatePicker,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
} from '@roger/ui-components';
import {
  WOUND_TYPES,
  getDeepestStageOptions,
  getStageLabel,
  getStageOptions,
} from '../../constants/wounds';
import { getWoundAttachmentUrl, getWoundAttachments } from '../../services/api';
import type {
  WoundAttachmentResponse,
  WoundResponse,
} from '../../services/api';
import { useStore } from '../../store';
import WoundBodyMap, { type BodyMapPoint } from './WoundBodyMap';
import WoundTypeCombobox from './WoundTypeCombobox';
import { toDateStr } from './woundUtils';
import {
  formatDateToMMDDYYYY,
  parseISOToLocalDate,
} from '../../utils/dateUtils';
import {
  isBrowserRenderableImage,
  isDownloadOnlyImage,
} from '../../utils/browserImage';
import WoundAttachmentRow from './WoundAttachmentRow';
import { openWoundAttachment } from './openWoundAttachment';

const DISCONTINUE_REASONS = [
  { value: 'wound_healed', label: 'Wound Healed' },
  { value: 'change_in_care', label: 'Change in Care' },
  { value: 'patient_refusal', label: 'Patient Refusal' },
  { value: 'other', label: 'Other' },
];

interface WoundFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingWound: WoundResponse | null;
  canDiscontinue?: boolean;
  onSave: (data: {
    location: string;
    wound_type: string;
    onset_date: string | null;
    present_on_admission: boolean;
    body_map_point?: { x: number; y: number } | null;
    wound_stage?: string | null;
    /** Present only when the clinician corrected the deepest stage. */
    deepest_stage?: string | null;
    discontinued_date?: string | null;
    discontinued_reason?: string | null;
    discontinued_comments?: string | null;
  }) => Promise<void>;
  /**
   * The patient's discontinued pressure injuries, offered for reactivation
   * when a pressure injury is being added. Leave it off when editing.
   */
  discontinuedPressureInjuries?: WoundResponse[];
  /** Resolves once the attempt is over, whether or not it succeeded. */
  onReactivate?: (woundId: string) => Promise<void>;
}

const KNOWN_VALUES = new Set(WOUND_TYPES.map(t => t.value));

// Radix Select has no empty item value, so this stands in for no stage.
const NO_STAGE = 'none';

interface StageSelectProps {
  id: string;
  label: string;
  placeholder: string;
  /** The stored stage, or an empty string for none. */
  value: string;
  onChange: (value: string) => void;
  noneLabel: string;
  options: { value: string; label: string }[];
  help?: string;
}

const StageSelect: React.FC<StageSelectProps> = ({
  id,
  label,
  placeholder,
  value,
  onChange,
  noneLabel,
  options,
  help,
}) => (
  <div className='space-y-1.5'>
    <Label htmlFor={id}>{label}</Label>
    <Select
      value={value || NO_STAGE}
      onValueChange={val => onChange(val === NO_STAGE ? '' : val)}
    >
      <SelectTrigger id={id} selectSize='sm'>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NO_STAGE}>{noneLabel}</SelectItem>
        {options.map(o => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
    {help && <p className='text-xs text-muted-foreground'>{help}</p>}
  </div>
);

const WoundFormDialog: React.FC<WoundFormDialogProps> = ({
  open,
  onOpenChange,
  editingWound,
  canDiscontinue = false,
  onSave,
  discontinuedPressureInjuries,
  onReactivate,
}) => {
  const [location, setLocation] = useState('');
  const [woundType, setWoundType] = useState('');
  const [otherDescription, setOtherDescription] = useState('');
  const [woundStage, setWoundStage] = useState('');
  const [deepestStage, setDeepestStage] = useState('');
  const [onsetDate, setOnsetDate] = useState<Date | undefined>(undefined);
  const [presentOnAdmission, setPresentOnAdmission] = useState(false);
  const [bodyMapPoint, setBodyMapPoint] = useState<BodyMapPoint | null>(null);
  const [discontinuedDate, setDiscontinuedDate] = useState<Date | undefined>(
    undefined
  );
  const [discontinuedReason, setDiscontinuedReason] = useState('');
  const [discontinuedReasonOther, setDiscontinuedReasonOther] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);
  // State alone lags a second click that lands before the re-render.
  const reactivateInFlight = useRef(false);
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [woundImagePreviews, setWoundImagePreviews] = useState<
    { id: string; fileName: string; url: string }[]
  >([]);
  const [hasImageLoadError, setHasImageLoadError] = useState(false);
  // HEIC and TIFF photos: no preview, so they show as download rows.
  const [downloadOnlyImages, setDownloadOnlyImages] = useState<
    WoundAttachmentResponse[]
  >([]);

  const showError = useStore(state => state.showError);
  const imageErrorToastShownRef = useRef(false);

  useEffect(() => {
    if (open) {
      if (editingWound) {
        setLocation(editingWound.location);
        if (KNOWN_VALUES.has(editingWound.wound_type)) {
          setWoundType(editingWound.wound_type);
          setOtherDescription('');
        } else {
          setWoundType('other');
          setOtherDescription(editingWound.wound_type);
        }
        setWoundStage(editingWound.wound_stage ?? '');
        setDeepestStage(editingWound.deepest_stage ?? '');
        // Two facts, each its own field (EMR-1828): a wound present on
        // admission can still have a known onset date.
        setOnsetDate(
          editingWound.onset_date
            ? parseISOToLocalDate(editingWound.onset_date)
            : undefined
        );
        setPresentOnAdmission(editingWound.present_on_admission);
        const bmp = (
          editingWound as WoundResponse & {
            body_map_point?: { x: number; y: number } | null;
          }
        ).body_map_point;
        setBodyMapPoint(bmp ?? null);
        setDiscontinuedDate(
          editingWound.discontinued_date
            ? parseISOToLocalDate(editingWound.discontinued_date)
            : undefined
        );
        const storedReason =
          (
            editingWound as WoundResponse & {
              discontinued_reason?: string | null;
            }
          ).discontinued_reason ?? '';
        if (storedReason === 'other') {
          setDiscontinuedReason('other');
          setDiscontinuedReasonOther(
            (
              editingWound as WoundResponse & {
                discontinued_comments?: string | null;
              }
            ).discontinued_comments ?? ''
          );
        } else {
          setDiscontinuedReason(storedReason);
          setDiscontinuedReasonOther('');
        }
      } else {
        setLocation('');
        setWoundType('');
        setOtherDescription('');
        setWoundStage('');
        setDeepestStage('');
        setOnsetDate(undefined);
        setPresentOnAdmission(false);
        setBodyMapPoint(null);
        setDiscontinuedDate(undefined);
        setDiscontinuedReason('');
        setDiscontinuedReasonOther('');
      }
    }
  }, [open, editingWound]);

  useEffect(() => {
    let isCancelled = false;

    const loadWoundImages = async () => {
      if (!open || !editingWound) {
        setIsLoadingImages(false);
        setWoundImagePreviews([]);
        setDownloadOnlyImages([]);
        setHasImageLoadError(false);
        imageErrorToastShownRef.current = false;
        return;
      }

      setIsLoadingImages(true);
      setHasImageLoadError(false);
      try {
        const attachmentsResp = await getWoundAttachments({
          path: {
            patient_id: editingWound.patient_id,
            wound_id: editingWound.id,
          },
        });
        // The client returns { error } instead of throwing; without this a
        // failed list reads as a wound with no images.
        if (attachmentsResp.error) throw attachmentsResp.error;
        const attachments =
          (attachmentsResp.data as WoundAttachmentResponse[]) ?? [];
        const imageAttachments = attachments.filter(a =>
          isBrowserRenderableImage(a.mime_type)
        );
        if (!isCancelled) {
          setDownloadOnlyImages(
            attachments.filter(a => isDownloadOnlyImage(a.mime_type))
          );
        }
        let hadUrlFailure = false;
        const previewItems = await Promise.all(
          imageAttachments.map(async attachment => {
            try {
              const urlResp = await getWoundAttachmentUrl({
                path: {
                  patient_id: editingWound.patient_id,
                  wound_id: editingWound.id,
                  attachment_id: attachment.id,
                },
              });
              const data = urlResp.data as { url?: string };
              if (!data.url) {
                hadUrlFailure = true;
                return null;
              }
              return {
                id: attachment.id,
                fileName: attachment.file_name,
                url: data.url,
              };
            } catch (error) {
              console.error('Failed to load wound attachment URL:', error);
              hadUrlFailure = true;
              return null;
            }
          })
        );
        if (!isCancelled) {
          setWoundImagePreviews(
            previewItems.filter(
              (item): item is { id: string; fileName: string; url: string } =>
                item !== null
            )
          );
          if (hadUrlFailure) {
            setHasImageLoadError(true);
            if (!imageErrorToastShownRef.current) {
              imageErrorToastShownRef.current = true;
              showError(
                'Unable to load image preview',
                'Some wound images could not be displayed. Please refresh or try again.'
              );
            }
          }
        }
      } catch (error) {
        console.error('Failed to load wound attachments:', error);
        if (!isCancelled) {
          setWoundImagePreviews([]);
          setDownloadOnlyImages([]);
          setHasImageLoadError(true);
          if (!imageErrorToastShownRef.current) {
            imageErrorToastShownRef.current = true;
            showError(
              'Unable to load wound images',
              'Something went wrong. Please refresh or try again.'
            );
          }
        }
      } finally {
        if (!isCancelled) setIsLoadingImages(false);
      }
    };

    loadWoundImages();

    return () => {
      isCancelled = true;
    };
  }, [open, editingWound, showError]);

  const handleDownloadImage = async (attachment: WoundAttachmentResponse) => {
    if (!editingWound) return;
    await openWoundAttachment(
      editingWound,
      attachment,
      showError,
      'Failed to download image. Please try again.'
    );
  };

  const downloadOnlyRows =
    downloadOnlyImages.length > 0 ? (
      <div className='space-y-1.5'>
        <p className='text-xs text-muted-foreground'>
          {downloadOnlyImages.length === 1
            ? "1 photo can't be previewed. Download it to view."
            : `${downloadOnlyImages.length} photos can't be previewed. Download them to view.`}
        </p>
        {downloadOnlyImages.map(attachment => (
          <WoundAttachmentRow
            key={attachment.id}
            attachment={attachment}
            onDownload={handleDownloadImage}
          />
        ))}
      </div>
    ) : null;

  const handleWoundTypeChange = (val: string) => {
    if (val === woundType) return;
    setWoundType(val);
    if (val !== 'other') setOtherDescription('');
    // A stage does not carry from one type to another.
    setWoundStage('');
  };

  const effectiveWoundType =
    woundType === 'other' ? otherDescription.trim() : woundType;

  // Keyed on the picked type, not the free-text "other" description, so typing
  // a staged type's name there never brings up a stage list.
  const stageOptions = getStageOptions(woundType);
  const deepestStageOptions = getDeepestStageOptions(woundType);
  const stageNoun = woundType === 'burn' ? 'degree' : 'stage';
  // The deepest stage is the wound's history under its saved type. Once the
  // type changes the server starts it over, so there is nothing to correct.
  const savedDeepestStage = editingWound?.deepest_stage ?? '';
  const showDeepestStage =
    !!editingWound && !!stageOptions && woundType === editingWound.wound_type;
  const deepestStageChanged =
    showDeepestStage && deepestStage !== savedDeepestStage;
  // An edit sends the stage only when it or the type changed, so saving an
  // unrelated field never writes back a stage this dialog loaded earlier.
  const stageChanged =
    !editingWound ||
    woundStage !== (editingWound.wound_stage ?? '') ||
    effectiveWoundType !== editingWound.wound_type;

  const discontinuedValid =
    !discontinuedDate ||
    (!!discontinuedReason &&
      (discontinuedReason !== 'other' ||
        discontinuedReasonOther.trim().length > 0));

  const isValid =
    location.trim().length > 0 &&
    woundType.length > 0 &&
    (woundType !== 'other' || otherDescription.trim().length > 0) &&
    discontinuedValid;

  const handleSave = async () => {
    if (!isValid) return;
    setIsSaving(true);
    try {
      await onSave({
        location: location.trim(),
        wound_type: effectiveWoundType,
        onset_date: toDateStr(onsetDate),
        present_on_admission: presentOnAdmission,
        body_map_point: bodyMapPoint,
        ...(stageChanged ? { wound_stage: woundStage || null } : {}),
        // Sent only as a correction; left out, the server maintains it. Null
        // resets it to the wound's current stage.
        ...(deepestStageChanged ? { deepest_stage: deepestStage || null } : {}),
        ...(editingWound
          ? {
              discontinued_date: toDateStr(discontinuedDate),
              discontinued_reason: discontinuedReason || null,
              discontinued_comments:
                discontinuedReason === 'other'
                  ? discontinuedReasonOther.trim() || null
                  : null,
            }
          : {}),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const priorPressureInjuries =
    !editingWound && woundType === 'pressure_injury'
      ? (discontinuedPressureInjuries ?? [])
      : [];

  const handleReactivate = async (woundId: string) => {
    if (!onReactivate || reactivateInFlight.current) return;
    reactivateInFlight.current = true;
    setIsReactivating(true);
    try {
      await onReactivate(woundId);
    } finally {
      reactivateInFlight.current = false;
      setIsReactivating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{editingWound ? 'Edit Wound' : 'Add Wound'}</DialogTitle>
        </DialogHeader>

        <div className='grid grid-cols-2 gap-6 py-2'>
          {/* Left column: fields */}
          <div className='space-y-4'>
            {/* Location */}
            <div className='space-y-1.5'>
              <Label>
                Location <span className='text-destructive'>*</span>
              </Label>
              <Input
                placeholder='e.g. Left heel, Sacrum'
                value={location}
                onChange={e => setLocation(e.target.value)}
              />
            </div>

            {/* Wound Type */}
            <div className='space-y-1.5'>
              <Label>
                Wound Type <span className='text-destructive'>*</span>
              </Label>
              <WoundTypeCombobox
                value={woundType}
                onValueChange={handleWoundTypeChange}
                onAddCustomType={text => {
                  handleWoundTypeChange('other');
                  setOtherDescription(text);
                }}
              />
              {woundType === 'other' && (
                <Input
                  autoFocus
                  placeholder='Describe the wound type...'
                  value={otherDescription}
                  onChange={e => setOtherDescription(e.target.value)}
                />
              )}
            </div>

            {/* Prior pressure injuries: add mode only */}
            {priorPressureInjuries.length > 0 && (
              <div className='space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3'>
                <p className='text-sm font-medium text-amber-800'>
                  Prior pressure injuries
                </p>
                <p className='text-xs text-amber-800'>
                  If this wound is at the same location as a prior one,
                  reactivate it to keep its staging history.
                </p>
                <ul className='space-y-2'>
                  {priorPressureInjuries.map(prior => {
                    const priorStage = getStageLabel(
                      prior.deepest_stage ?? prior.wound_stage,
                      prior.wound_type
                    );
                    return (
                      <li
                        key={prior.id}
                        className='flex flex-wrap items-center justify-between gap-2'
                      >
                        <div className='min-w-0 text-sm'>
                          <p className='break-words font-medium'>
                            {prior.location}
                            {priorStage && ` · ${priorStage}`}
                          </p>
                          {prior.discontinued_date && (
                            <p className='text-xs text-muted-foreground'>
                              Discontinued{' '}
                              {formatDateToMMDDYYYY(prior.discontinued_date)}
                            </p>
                          )}
                        </div>
                        <Button
                          type='button'
                          variant='outline'
                          size='sm'
                          onClick={() => handleReactivate(prior.id)}
                          disabled={isReactivating || isSaving}
                        >
                          Reactivate
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* Stage (pressure injury) or degree (burn) */}
            {stageOptions && (
              <StageSelect
                id='wound-stage'
                label={stageNoun === 'degree' ? 'Degree' : 'Stage'}
                placeholder={`Select ${stageNoun}...`}
                value={woundStage}
                onChange={setWoundStage}
                noneLabel='Not recorded'
                options={stageOptions}
              />
            )}

            {/* Deepest stage correction: edit mode only */}
            {showDeepestStage && deepestStageOptions && (
              <StageSelect
                id='wound-deepest-stage'
                label={`Deepest ${stageNoun}`}
                placeholder={`Select ${stageNoun}...`}
                value={deepestStage}
                onChange={setDeepestStage}
                noneLabel={
                  deepestStageOptions.some(o => o.value === woundStage)
                    ? `Same as current ${stageNoun}`
                    : 'Not recorded'
                }
                options={deepestStageOptions}
                help={`The deepest ${stageNoun} this wound has reached. Change it only to correct a recording error.`}
              />
            )}

            {/* Onset date and present on admission: two facts, each its own
                field. A wound present on admission can still have a known
                onset date; a wound acquired later can have an unknown one. */}
            <div className='space-y-1.5'>
              <Label>Onset date</Label>
              <DatePicker
                date={onsetDate}
                onDateChange={setOnsetDate}
                placeholder='Leave blank if unknown'
                datePickerSize='default'
              />
              <div className='flex items-center gap-2 mt-1'>
                <Checkbox
                  id='present-on-admission'
                  checked={presentOnAdmission}
                  onCheckedChange={checked => setPresentOnAdmission(!!checked)}
                />
                <label
                  htmlFor='present-on-admission'
                  className='text-sm text-muted-foreground cursor-pointer'
                >
                  Present on admission
                </label>
              </div>
            </div>

            {/* Discontinued — edit mode + visit context only */}
            {editingWound && canDiscontinue && (
              <>
                <Separator />
                <div className='space-y-3'>
                  <Label className='text-sm font-medium text-muted-foreground uppercase tracking-wide'>
                    Discontinuation
                  </Label>
                  <div className='space-y-1.5'>
                    <Label>Date Discontinued</Label>
                    <DatePicker
                      date={discontinuedDate}
                      onDateChange={d => {
                        setDiscontinuedDate(d);
                        if (!d) {
                          setDiscontinuedReason('');
                          setDiscontinuedReasonOther('');
                        }
                      }}
                      placeholder='Select date'
                      datePickerSize='default'
                      showClearButton
                    />
                  </div>
                  {discontinuedDate && (
                    <>
                      <div className='space-y-1.5'>
                        <Label>Reason</Label>
                        <Select
                          value={discontinuedReason}
                          onValueChange={v => {
                            setDiscontinuedReason(v);
                            if (v !== 'other') setDiscontinuedReasonOther('');
                          }}
                        >
                          <SelectTrigger selectSize='sm'>
                            <SelectValue placeholder='Select reason...' />
                          </SelectTrigger>
                          <SelectContent>
                            {DISCONTINUE_REASONS.map(r => (
                              <SelectItem key={r.value} value={r.value}>
                                {r.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {discontinuedReason === 'other' && (
                        <div className='space-y-1.5'>
                          <Label>Please specify</Label>
                          <Input
                            placeholder='Describe reason...'
                            value={discontinuedReasonOther}
                            onChange={e =>
                              setDiscontinuedReasonOther(e.target.value)
                            }
                            inputSize='sm'
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Right column: body map */}
          <div className='space-y-1.5'>
            <Label>Body Location</Label>
            <WoundBodyMap value={bodyMapPoint} onChange={setBodyMapPoint} />
            {editingWound && (
              <div className='pt-2 space-y-1.5'>
                <Label>Uploaded Images</Label>
                {isLoadingImages ? (
                  <div className='flex items-center gap-2 text-xs text-muted-foreground'>
                    <Loader2 className='h-3.5 w-3.5 animate-spin' />
                    Loading wound images...
                  </div>
                ) : woundImagePreviews.length === 0 ? (
                  <>
                    {(hasImageLoadError || !downloadOnlyRows) && (
                      <p className='text-xs text-muted-foreground'>
                        {!hasImageLoadError
                          ? 'No wound images uploaded yet.'
                          : downloadOnlyRows
                            ? 'Some wound images could not be displayed. Please refresh or try again.'
                            : 'Unable to load wound images. Please refresh or try again.'}
                      </p>
                    )}
                    {downloadOnlyRows}
                  </>
                ) : (
                  <>
                    <div className='grid grid-cols-2 gap-2'>
                      {woundImagePreviews.slice(0, 4).map(image => (
                        <button
                          key={image.id}
                          type='button'
                          className='group text-left'
                          onClick={() => window.open(image.url, '_blank')}
                        >
                          <img
                            src={image.url}
                            alt={image.fileName}
                            className='h-24 w-full rounded-md border object-cover'
                          />
                          <p className='mt-1 text-xs text-muted-foreground truncate'>
                            {image.fileName}
                          </p>
                        </button>
                      ))}
                    </div>
                    {hasImageLoadError && (
                      <p className='text-xs text-destructive'>
                        Some wound images could not be displayed. Please refresh
                        or try again.
                      </p>
                    )}
                    {downloadOnlyRows}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={isSaving || isReactivating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!isValid || isSaving || isReactivating}
          >
            {isSaving && <Loader2 className='h-4 w-4 mr-1 animate-spin' />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WoundFormDialog;
